import { FeatureCollection, LineString, Polygon } from "geojson";
import { GuidePathStatus } from "protos/rtc/jobs";
import { guidePathStatusToLocaleString } from "common/utils/enumTranslations";
import { Layer, Marker, Source } from "react-map-gl";
import { lineString } from "@turf/helpers";
import { makeTaskRenderer } from "../TaskLayers";
import { Point } from "protos/geo/geo";
import { TASK_BASE_PROPS, TaskFeatureProperties } from "common/utils/rtcJobs";
import { resolvedTheme as theme } from "common/theme/tailwind.theme";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import bearing from "@turf/bearing";
import buffer from "@turf/buffer";
import destination from "@turf/destination";
import React, { useMemo } from "react";
import WarningIcon from "@mui/icons-material/WarningAmber";

// How far to nudge the warning icon (and the drawn guide path) off
// the row's endpoint, so they don't sit on top of the row numbers.
const WARNING_OFFSET_M = 6;

// Compute the centroid of a path's points, used as the row center.
function rowCenter(
  points: Point[] | undefined
): { lng: number; lat: number } | undefined {
  if (!points || points.length === 0) {
    return undefined;
  }
  const sumLat = points.reduce((sum, p) => sum + p.lat, 0);
  const sumLng = points.reduce((sum, p) => sum + p.lng, 0);
  return { lng: sumLng / points.length, lat: sumLat / points.length };
}

// Nudge a point off the edge of the field
function offsetAwayFromRow(
  point: Point,
  heading: number,
  offsetM: number
): Point {
  const [lng, lat] = destination([point.lng, point.lat], offsetM, heading, {
    units: "meters",
  }).geometry.coordinates;
  return { ...point, lng: lng ?? point.lng, lat: lat ?? point.lat };
}

// Only show the guide path warning if it's in an error state
function isGuidePathWarning(status: GuidePathStatus): boolean {
  return (
    status !== GuidePathStatus.GUIDE_PATH_STATUS_UNSPECIFIED &&
    status !== GuidePathStatus.GUIDE_PATH_STATUS_OK &&
    status !== GuidePathStatus.UNRECOGNIZED
  );
}

enum LayerIds {
  PATH = "grpt-path",
  GUIDE_PATH = "grpt-guide-path",
  GUIDE_PATH_OUTLINE = "grpt-guide-path-outline",
  GUIDE_PATH_HIT_AREA = "grpt-guide-path-hit-area",
  GUIDE_PATH_ARROWS = "grpt-guide-path-arrows",
}

export const renderGoToReversiblePathTask = makeTaskRenderer({
  detailsField: "goToReversiblePath",
  Render: ({ tasks, extraLayerProps, beforeId, showTechnicalDetails }) => {
    const { t } = useTranslation();
    const data: {
      paths: FeatureCollection<LineString, TaskFeatureProperties>;
      guidePaths: FeatureCollection<LineString, TaskFeatureProperties>;
      guidePathOutlines: FeatureCollection<Polygon, TaskFeatureProperties>;
    } = useMemo(() => {
      const paths: FeatureCollection<LineString, TaskFeatureProperties> = {
        type: "FeatureCollection",
        features: [],
      };

      const guidePaths: FeatureCollection<LineString, TaskFeatureProperties> = {
        type: "FeatureCollection",
        features: [],
      };

      const guidePathOutlines: FeatureCollection<
        Polygon,
        TaskFeatureProperties
      > = {
        type: "FeatureCollection",
        features: [],
      };

      for (const task of tasks) {
        const taskData = task.goToReversiblePath;

        if (taskData.path?.points) {
          paths.features.push({
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: taskData.path.points.map((pt) => [pt.lng, pt.lat]),
            },
            properties: {
              ...TASK_BASE_PROPS,
              task,
            },
          });
        }

        if (
          taskData.guidePath?.points &&
          taskData.guidePath.points.length >= 2
        ) {
          const center = isGuidePathWarning(taskData.guidePathStatus)
            ? rowCenter(taskData.path?.points)
            : undefined;
          const coords = taskData.guidePath.points.map((pt) => {
            if (center === undefined) {
              return [pt.lng, pt.lat];
            }
            const heading = bearing([center.lng, center.lat], [pt.lng, pt.lat]);
            // Offset the drawn guide path by the same amount as the warning icon
            const p = offsetAwayFromRow(pt, heading, WARNING_OFFSET_M);
            return [p.lng, p.lat];
          });
          guidePaths.features.push({
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: coords,
            },
            properties: {
              ...TASK_BASE_PROPS,
              task,
            },
          });

          // Create outline by buffering the path
          const buffered = buffer(lineString(coords), 1, {
            units: "meters",
            steps: 2,
          });
          guidePathOutlines.features.push({
            ...buffered,
            properties: {
              ...TASK_BASE_PROPS,
              task,
            },
          });
        }
      }

      return { paths, guidePaths, guidePathOutlines };
    }, [tasks]);

    // Warn on any reversible path whose guide path failed to plan.
    const guidePathWarnings = useMemo(
      () =>
        tasks.flatMap((task) => {
          const taskData = task.goToReversiblePath;
          if (!isGuidePathWarning(taskData.guidePathStatus)) {
            return [];
          }
          // Nudge the icons off the endpoints, away from the row center,
          // matching the offset applied to the drawn guide path.
          const center = rowCenter(taskData.path?.points);
          const offset = (pt: Point): Point => {
            if (center === undefined) {
              return pt;
            }
            const heading = bearing([center.lng, center.lat], [pt.lng, pt.lat]);
            return offsetAwayFromRow(pt, heading, WARNING_OFFSET_M);
          };

          // If we received a guide path with the warning, it tells us the start
          // and end of the path we were trying to plan. Mark both of the ends
          // with an icon in case they're far apart. If we don't have a path,
          // fall back to the one end of the row it's associated with.
          const guidePoints = taskData.guidePath?.points;
          const firstGuidePoint = guidePoints?.[0];
          const lastGuidePoint = guidePoints?.at(-1);
          let anchors: Point[];
          if (
            guidePoints &&
            guidePoints.length >= 2 &&
            firstGuidePoint &&
            lastGuidePoint
          ) {
            anchors = [firstGuidePoint, lastGuidePoint];
          } else {
            const start = taskData.path?.points[0];
            if (!start) {
              return [];
            }
            anchors = [start];
          }

          const status = guidePathStatusToLocaleString(
            t,
            taskData.guidePathStatus
          );
          const errorDetails = taskData.guidePathErrorDetails;
          return anchors.map((anchor, i) => ({
            id: `${task.id}-${i}`,
            point: offset(anchor),
            status,
            errorDetails,
          }));
        }),
      [tasks, t]
    );

    return (
      <>
        {guidePathWarnings.map(({ id, point, status, errorDetails }) => (
          <Marker
            key={id}
            longitude={point.lng}
            latitude={point.lat}
            style={{ cursor: "pointer" }}
          >
            <Tooltip
              title={
                <>
                  <div>{status}</div>
                  {showTechnicalDetails && errorDetails && (
                    // We don't need to translate for Carbon technical users
                    // eslint-disable-next-line i18next/no-literal-string
                    <div>Details: {errorDetails}</div>
                  )}
                </>
              }
              slotProps={{ tooltip: { className: "bg-slate-900/60" } }}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900/60">
                <WarningIcon color="error" className="h-8 w-8" />
              </div>
            </Tooltip>
          </Marker>
        ))}
        <Source data={data.paths} type="geojson">
          <Layer
            id={LayerIds.PATH}
            beforeId={beforeId}
            type="line"
            paint={{
              "line-color": theme.colors.black,
              "line-dasharray": [16, 8],
            }}
            {...extraLayerProps}
          />
        </Source>
        {data.guidePaths.features.length > 0 && (
          <>
            <Source data={data.guidePathOutlines} type="geojson">
              <Layer
                id={LayerIds.GUIDE_PATH_HIT_AREA}
                beforeId={beforeId}
                type="fill"
                paint={{
                  "fill-color": theme.colors.black,
                  "fill-opacity": 0.01,
                }}
                {...extraLayerProps}
              />
              <Layer
                id={LayerIds.GUIDE_PATH_OUTLINE}
                beforeId={beforeId}
                type="line"
                paint={{
                  "line-color": theme.colors.black,
                }}
                {...extraLayerProps}
              />
            </Source>
            <Source data={data.guidePaths} type="geojson">
              <Layer
                id={LayerIds.GUIDE_PATH}
                beforeId={beforeId}
                type="line"
                paint={{
                  "line-color": theme.colors.black,
                  "line-dasharray": [16, 8],
                }}
                {...extraLayerProps}
              />
              <Layer
                id={LayerIds.GUIDE_PATH_ARROWS}
                beforeId={beforeId}
                type="symbol"
                layout={{
                  "symbol-placement": "line",
                  "symbol-spacing": 100,
                  "text-field": "▶",
                  "text-size": 40,
                  "text-keep-upright": false,
                  "text-rotation-alignment": "map",
                }}
                paint={{
                  "text-color": theme.colors.black,
                  "text-opacity": 0.8,
                }}
                {...extraLayerProps}
              />
            </Source>
          </>
        )}
      </>
    );
  },
  interactiveLayerIds: [LayerIds.PATH, LayerIds.GUIDE_PATH_HIT_AREA],
  imagesToLoad: [],
});
